import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentOrganizationDepartmentList } from '../../../../../store/selectors/organizations';
import { IClaimLinkTemplate, ITemplateCardConfig } from '../../../../../types/templates';
import { getDepartmentConfirmMessage, hasDepartmentRestrictions } from '../../../../../utils/templates';

export interface IUseTemplateSelectionOptions {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: IClaimLinkTemplate) => void;
  currentTemplateId?: string;
  campaignTemplateId?: string;
  showDepartmentDialog?: boolean;
}

export const useTemplateSelection = ({
  isOpen,
  onClose,
  onSelect,
  currentTemplateId,
  campaignTemplateId,
  showDepartmentDialog = false,
}: IUseTemplateSelectionOptions) => {
  const [newTemplate, setNewTemplate] = useState<IClaimLinkTemplate | null>(null);
  const [isDepartmentConfirmOpen, setIsDepartmentConfirmOpen] = useState(false);
  const departmentList = useSelector(selectCurrentOrganizationDepartmentList) ?? [];

  const handleSelect = useCallback((template: IClaimLinkTemplate) => {
    setNewTemplate(template);
  }, []);

  const handleDepartmentConfirmClose = useCallback(() => setIsDepartmentConfirmOpen(false), []);

  const hasDeptRestrictions = useMemo(() => hasDepartmentRestrictions(newTemplate?.department_ids), [newTemplate]);

  const confirmSubmit = useCallback(() => {
    if (newTemplate) {
      onSelect(newTemplate);
    }
    setIsDepartmentConfirmOpen(false);
    onClose();
  }, [newTemplate, onSelect, onClose]);

  // Re-picking the already-selected template isn't a change, so it shouldn't prompt the
  // "Set as organization default?" confirmation — only a switch to a different template does.
  const hasSelectionChanged = useMemo(
    () => !!newTemplate && newTemplate.uid !== currentTemplateId,
    [newTemplate, currentTemplateId],
  );

  const handleSubmit = useCallback(() => {
    if (showDepartmentDialog && hasSelectionChanged && hasDeptRestrictions) {
      setIsDepartmentConfirmOpen(true);
      return;
    }
    confirmSubmit();
  }, [showDepartmentDialog, hasSelectionChanged, hasDeptRestrictions, confirmSubmit]);

  const departmentConfirmMessage = useMemo(
    () => getDepartmentConfirmMessage(newTemplate?.department_ids ?? [], departmentList),
    [newTemplate, departmentList],
  );

  // An effective selection is a template picked in the modal (including re-picking the same one)
  // or the template that was already selected before opening. useFilters keeps Done enabled without
  // a selection only when the org genuinely has no templates (count 0 AND no filters applied), so a
  // filtered-to-empty result can't wrongly enable Done.
  const hasSelection = useMemo(() => !!newTemplate || !!currentTemplateId, [newTemplate, currentTemplateId]);

  const newTemplateId = useMemo(() => newTemplate?.uid, [newTemplate?.uid]);

  const cardConfig: ITemplateCardConfig = useMemo(
    () => ({
      showStatusChip: false,
      enableEditDetails: false,
      highlightDefault: true,
      campaignClaimLinkTemplateId: campaignTemplateId,
      onCardClick: handleSelect,
      isHighlighted: (template: IClaimLinkTemplate) => {
        return newTemplateId ? template.uid === newTemplateId : template.uid === currentTemplateId;
      },
      actions: (template: IClaimLinkTemplate) => {
        const isActivelySelected = template.uid === newTemplateId;
        const isCurrentlySaved = !newTemplateId && template.uid === currentTemplateId;
        const isSelected = isActivelySelected || isCurrentlySaved;

        return (
          <Button
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={() => handleSelect(template)}
            startIcon={isSelected && <CheckCircleIcon />}
            data-action="choose_template_btn"
          >
            {isSelected ? 'Selected' : 'Choose template'}
          </Button>
        );
      },
    }),
    [handleSelect, newTemplateId, currentTemplateId, campaignTemplateId],
  );

  useEffect(() => {
    if (isOpen) {
      setNewTemplate(null);
    }
  }, [isOpen]);

  return {
    cardConfig,
    hasSelection,
    handleSubmit,
    confirmSubmit,
    isDepartmentConfirmOpen,
    handleDepartmentConfirmClose,
    departmentConfirmMessage,
  };
};
